import React, { useState, useContext } from 'react';
import { IoEye, IoEyeOff } from "react-icons/io5";
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_END_POINT } from '../pages/Utils/Constant';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logoimg from "../assets/rootimg/astrotellerlogo.png";
import { AuthContext } from '../pages/AuthContext';

import CommonNavbar from '../pages/CommonNavbar';

const Login = () => {
    const { login } = useContext(AuthContext);
    const [input, setInput] = useState({
        email: '',
        password: ''
    });
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInput(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const validate = () => {
        const errors = {};
        
        // Email validation
        if (!input.email) {
            errors.email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(input.email)) {
            errors.email = "Please enter a valid email address.";
        }

        // Password validation
        if (!input.password) {
            errors.password = "Password is required.";
        } else if (input.password.length < 6) {
            errors.password = "Password must be at least 6 characters long.";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
    
        if (!validate()) {
            setLoading(false);
            return;
        }
    
        try {
            const response = await axios.post(`${API_END_POINT}auth/login`, {
                email: input.email,
                password: input.password
            });
    
            // console.log('Login response:', response.data);
    
            // Use the login method from AuthContext
            login(response.data.token, response.data.role);
    
            setInput({
                email: '',
                password: ''
            });
    
            // Show success toast
            toast.success('Login successful!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            // If user is admin, set adminToken in localStorage
            if (response.data.role === 'admin') {
                localStorage.setItem('adminToken', response.data.token);
                localStorage.setItem('adminUser', JSON.stringify({
                    id: response.data.userId,
                    role: response.data.role,
                    email: input.email
                }));
            }

            // Navigate based on user role
            setTimeout(() => {
                // If user is admin, redirect to admin dashboard
                if (response.data.role === 'admin') {
                    navigate('/admin-dashboard');
                } else {
                    // Otherwise redirect to home
                    navigate('/');
                }
            }, 1000);
    
        } catch (err) {
            console.error("Error logging in:", err.response?.data || err.message);
    
            if (err.response && err.response.status === 404) {
                toast.error("User Account not found.", {
                    position: "top-right",
                    autoClose: 5000,
                });
            } else if (err.response && err.response.status === 401) {
                toast.error("Invalid credentials. Please check your email and password.", {
                    position: "top-right",
                    autoClose: 5000,
                });
            } else {
                toast.error(err.response?.data?.message || "Something went wrong. Please try again later.", {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
            setError(err.response?.data?.message || "Something went wrong. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setPasswordVisible(prev => !prev);
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-white to-success/40">
            <CommonNavbar />
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <div className="w-full max-w-md p-8 mt-10 bg-white rounded-lg shadow-lg">
                {/* Logo */}
                <div className="flex justify-center mb-2">
                    <div className="w-[75%] h-auto mx-auto">
                        <img src={logoimg} alt="Logo" className="w-full h-full object-contain"/>
                    </div>
                </div>

                {/* Heading */}
                <h1 className="text-lg font-semibold text-center text-secondary">
                Unlock Your Cosmic Journey
                </h1>
                <p className="text-gray-600 text-center mb-4">
                    Please enter your details
                </p>

                <form onSubmit={handleSubmit}>
                    {/* Email Input */}
                    <div className="mb-4">
                        <input
                            type="email"
                            placeholder="Email"
                            name="email"
                            value={input.email}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#6366f1]"
                        />
                        {validationErrors.email && (
                            <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
                        )}
                    </div>

                    {/* Password Input */}
                    <div className="mb-2 relative">
                        <div className="relative">
                            <input
                                type={passwordVisible ? "text" : "password"}
                                placeholder="Password"
                                name="password"
                                value={input.password}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#6366f1]"
                            />
                            <button
                                type="button"
                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                onClick={togglePasswordVisibility}
                            >
                                {passwordVisible ? <IoEye size={20} color="#6B7280" /> : <IoEyeOff size={20} color="#6B7280" />}
                            </button>
                        </div>
                        {validationErrors.password && (
                            <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>
                        )}
                    </div>
                    
                    {/* forgot password */}
                    <div className="mb-1 ml-1 text-left">
                        <Link to="/forgot-password" className="text-gray-500 hover:text-secondary font-medium">
                            Forgot Password?
                        </Link>
                    </div>
                    {/* Error Message */}
                    {error && <p className="text-red-500 text-center mb-4">{error}</p>}

                    {/* Login Button */}
                    <button
                        type="submit"
                        className="w-full bg-black text-white py-3 rounded-md font-medium hover:bg-secondary transition-colors"
                        disabled={loading}
                    >
                        {loading ? "Logging in..." : "Login"}
                    </button>

                    {/* Sign Up Link */}
                    <p className="text-gray-600 text-center mt-6">
                        Don't have an account?{" "}
                        <Link to="/signup" className="text-blue-500 font-medium">
                            Sign Up
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default Login;
